import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Target } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTeamDisplayName } from '../../utils/teamDisplayName';
import { BetInput } from '../dashboard/BetInput';
import { Button } from '../ui/Button';
import { ScoreText } from '../ui/ScoreText';

function scoresFromBet(bet) {
  const pred = bet?.predictedResult;
  if (pred && typeof pred === 'object' && pred.homeScore != null) {
    return {
      homeScore: String(pred.homeScore),
      awayScore: String(pred.awayScore)
    };
  }
  if (typeof pred === 'string' && pred.includes(':')) {
    const [homeScore, awayScore] = pred.split(':');
    return { homeScore, awayScore };
  }
  return { homeScore: '', awayScore: '' };
}

export async function saveAdminUserBet({ userId, gameId, existingBetId, predictedResult }) {
  const resolvedUserId = userId?._id || userId;
  const resolvedGameId = gameId?._id || gameId;
  const resolvedBetId = existingBetId?._id || existingBetId;
  if (resolvedBetId) {
    const { data } = await axios.put(`/bets/admin/${resolvedBetId}`, {
      predictedResult,
      userId: resolvedUserId
    });
    return data.bet;
  }
  const { data } = await axios.post('/bets/admin', {
    gameId: resolvedGameId,
    userId: resolvedUserId,
    predictedResult
  });
  return data.bet;
}

/**
 * Admin modal to create or edit a user's prediction, including finished games.
 */
export function AdminBetScoreModal({
  userId,
  userName,
  game,
  existingBet,
  onClose,
  onSaved
}) {
  const { language, isRTL } = useLanguage();
  const initial = scoresFromBet(existingBet);
  const [homeScore, setHomeScore] = useState(initial.homeScore);
  const [awayScore, setAwayScore] = useState(initial.awayScore);
  const [submitting, setSubmitting] = useState(false);

  const finished = Boolean(game?.isFinished && game?.finalResult);
  const homeLabel = getTeamDisplayName(game?.homeGroupId, language) || (language === 'en' ? 'Home' : 'בית');
  const awayLabel = getTeamDisplayName(game?.awayGroupId, language) || (language === 'en' ? 'Away' : 'חוץ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (homeScore === '' || awayScore === '') {
      toast.error(language === 'en' ? 'Please enter both scores' : 'אנא הזן את שתי התוצאות');
      return;
    }
    const home = parseInt(homeScore, 10);
    const away = parseInt(awayScore, 10);
    if (Number.isNaN(home) || Number.isNaN(away) || home < 0 || away < 0) {
      toast.error(
        language === 'en'
          ? 'Scores must be valid non-negative numbers'
          : 'התוצאות חייבות להיות מספרים תקינים לא שליליים'
      );
      return;
    }

    if (finished) {
      const ok = window.confirm(
        language === 'en'
          ? "This game is finished and points have already been calculated. Saving will recalculate this user's points. Continue?"
          : 'המשחק הסתיים והנקודות כבר חושבו. השמירה תחשב מחדש את הנקודות למשתמש זה. להמשיך?'
      );
      if (!ok) return;
    }

    try {
      setSubmitting(true);
      const bet = await saveAdminUserBet({
        userId,
        gameId: game._id,
        existingBetId: existingBet?._id,
        predictedResult: { homeScore: home, awayScore: away }
      });
      if (finished) {
        toast.success(
          language === 'en'
            ? `Bet saved. Points updated (${bet?.points ?? 0}).`
            : `ההימור נשמר. הנקודות עודכנו (${bet?.points ?? 0}).`
        );
      } else if (existingBet) {
        toast.success(language === 'en' ? 'Bet updated successfully!' : 'ההימור עודכן בהצלחה!');
      } else {
        toast.success(language === 'en' ? 'Bet created successfully!' : 'ההימור נוצר בהצלחה!');
      }
      onSaved?.(bet);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (language === 'en' ? 'Failed to save bet' : 'נכשל בשמירת ההימור');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">
              {existingBet
                ? language === 'en'
                  ? 'Edit bet'
                  : 'ערוך הימור'
                : language === 'en'
                  ? 'Add bet'
                  : 'הוסף הימור'}
            </h2>
            <p className="mt-1 text-sm text-zinc-600">
              {userName}
              {game ? (
                <>
                  {' · '}
                  {homeLabel} vs {awayLabel}
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-zinc-100 p-2 text-zinc-700 hover:bg-zinc-200"
            aria-label={language === 'en' ? 'Close' : 'סגור'}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {finished ? (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {language === 'en'
              ? 'Game is finished. Saving will recalculate this user\'s points.'
              : 'המשחק הסתיים. השמירה תחשב מחדש את הנקודות למשתמש זה.'}
            <div className="mt-1 font-semibold">
              {language === 'en' ? 'Final:' : 'סופי:'}{' '}
              <ScoreText score={game.finalResult} />
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <BetInput
            homeValue={homeScore}
            awayValue={awayScore}
            onHomeChange={setHomeScore}
            onAwayChange={setAwayScore}
            homeLabel={homeLabel}
            awayLabel={awayLabel}
            idPrefix="admin-bet"
            isRTL={isRTL}
            disabled={submitting}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {language === 'en' ? 'Cancel' : 'ביטול'}
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              <Target className="h-4 w-4" />
              {existingBet
                ? language === 'en'
                  ? 'Save bet'
                  : 'שמור הימור'
                : language === 'en'
                  ? 'Create bet'
                  : 'צור הימור'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
